/**
 * Rewards permission codes.
 *
 * Platform permissions in the travel-admin `Travel:{Module}:{Action}` scheme
 * (Ticketon.Rewards README §5). `*`, `Travel:*` and `Travel:Rewards:*` grant all
 * of them. Exported from the framework-agnostic core so the admin UI (hiding
 * controls) and the admin app (route guards) share one source of truth.
 *
 * NOTE: Frontend checks are UX only. The backend enforces every operation with
 * a method-level `[AuthorizeResource]` carrying these same codes.
 */
export const RewardsPermission = {
  /** Dashboard, programs, tiers, rules, catalog, redemptions (read). */
  VIEW: "Travel:Rewards:View",
  /** Programs + tiers + promotions writes. */
  PROGRAMS_MANAGE: "Travel:Rewards:ManagePrograms",
  /**
   * Tier writes. The backend has no tier-specific code: tiers are governed by
   * `Travel:Rewards:ManagePrograms`, so this is an alias of `PROGRAMS_MANAGE`.
   */
  TIERS_MANAGE: "Travel:Rewards:ManagePrograms",
  /** Rule writes and rule simulation. */
  RULES_MANAGE: "Travel:Rewards:ManageRules",
  /** Catalog writes. */
  CATALOG_MANAGE: "Travel:Rewards:ManageCatalog",
  /** Member search, detail and history. */
  MEMBERS_VIEW: "Travel:Rewards:ViewMembers",
  /** Manual point adjustments. */
  MEMBERS_ADJUST: "Travel:Rewards:AdjustPoints",
  /** Fulfill / cancel / reverse redemptions. */
  REDEMPTIONS_MANAGE: "Travel:Rewards:ManageRedemptions",
  /** The rewards audit log. */
  AUDIT_VIEW: "Travel:Rewards:ViewAudit",
} as const;

/** A rewards permission code (`Travel:Rewards:*`). */
export type RewardsPermission = (typeof RewardsPermission)[keyof typeof RewardsPermission];

/** Every distinct rewards permission code (aliases collapsed). */
export const REWARDS_PERMISSIONS: readonly RewardsPermission[] = Array.from(
  new Set(Object.values(RewardsPermission)),
);

/** Namespace prefix shared by every rewards permission code. */
export const REWARDS_PERMISSION_PREFIX = "Travel:Rewards:";

/** Wildcards that grant every rewards permission. */
export const REWARDS_WILDCARDS = ["*", "Travel:*", "Travel:Rewards:*"] as const;

function hasRewardsWildcard(granted: readonly string[]): boolean {
  return granted.some((code) => (REWARDS_WILDCARDS as readonly string[]).includes(code));
}

/**
 * Returns true when the granted permission set includes `required`, honoring
 * the `*`, `Travel:*` and `Travel:Rewards:*` wildcards.
 */
export function hasRewardsPermission(
  granted: readonly string[],
  required: RewardsPermission,
): boolean {
  return hasRewardsWildcard(granted) || granted.includes(required);
}

/** Returns true when the permission set grants any rewards access at all. */
export function hasAnyRewardsAccess(granted: readonly string[]): boolean {
  return (
    hasRewardsWildcard(granted) ||
    granted.some((code) => code.startsWith(REWARDS_PERMISSION_PREFIX))
  );
}

/** The distinct rewards permissions a granted set resolves to (wildcards expanded). */
export function grantedRewardsPermissions(granted: readonly string[]): RewardsPermission[] {
  return REWARDS_PERMISSIONS.filter((code) => hasRewardsPermission(granted, code));
}

/** Capability flags derived from a granted permission set, for gating admin UI. */
export interface IRewardsGrants {
  canView: boolean;
  canManagePrograms: boolean;
  canManageTiers: boolean;
  canManageRules: boolean;
  canManageCatalog: boolean;
  canViewMembers: boolean;
  canAdjustPoints: boolean;
  canManageRedemptions: boolean;
  canViewAudit: boolean;
}

/**
 * Maps the caller's raw permission codes (as returned by the auth layer) to
 * rewards capability flags.
 *
 * @example
 * const grants = resolveRewardsGrants(user.permissions);
 * if (grants.canAdjustPoints) showAdjustButton();
 */
export function resolveRewardsGrants(granted: readonly string[]): IRewardsGrants {
  const can = (required: RewardsPermission) => hasRewardsPermission(granted, required);
  return {
    canView: can(RewardsPermission.VIEW),
    canManagePrograms: can(RewardsPermission.PROGRAMS_MANAGE),
    canManageTiers: can(RewardsPermission.TIERS_MANAGE),
    canManageRules: can(RewardsPermission.RULES_MANAGE),
    canManageCatalog: can(RewardsPermission.CATALOG_MANAGE),
    canViewMembers: can(RewardsPermission.MEMBERS_VIEW),
    canAdjustPoints: can(RewardsPermission.MEMBERS_ADJUST),
    canManageRedemptions: can(RewardsPermission.REDEMPTIONS_MANAGE),
    canViewAudit: can(RewardsPermission.AUDIT_VIEW),
  };
}
